import BaseComponent from '../base-component/base-component'
import {
  checkEnterKey,
  getFocusableTargets,
  _addEventListener,
  _removeEventListener,
  checkEscKey,
} from '../../utils/utils'
import FloatElement from './float-element'
import { Nullable } from '../../types/common'

class DropdownOpener extends BaseComponent {
  blurLastTargetTimeoutId: number
  blurElementTimeoutId: number
  element: HTMLElement

  constructor() {
    super()

    this.element = this.getElement()
  }

  connectedCallback(): void {
    if (this.floatElement) {
      customElements.upgrade(this.floatElement) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade

      this.floatElement.subscribe('show', this.handleDropdownShow)
      this.floatElement.subscribe('hide', this.handleDropdownHide)
    }

    this.element.addEventListener('blur', this.handleBlur)
    this.element.addEventListener('keyup', this.handleKeyUp)
    this.element.addEventListener('mousedown', this.handleTrigger)
  }

  disconnectedCallback(): void {
    if (this.floatElement) {
      customElements.upgrade(this.floatElement) // https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/upgrade

      this.floatElement.unsubscribe('show', this.handleDropdownShow)
      this.floatElement.unsubscribe('hide', this.handleDropdownHide)
    }

    this.element.removeEventListener('blur', this.handleBlur)
    this.element.removeEventListener('keyup', this.handleKeyUp)
    this.element.removeEventListener('mousedown', this.handleTrigger)

    window.removeEventListener('click', this.handleUntrigger)
  }

  handleBlur = (): void => {
    const { targets } = this.getTargets()

    this.blurElementTimeoutId = setTimeout(() => {
      if (!targets.includes(document.activeElement)) {
        if (!this.floatElement) {
          return
        }

        this.setTargetListeners('remove')

        this.floatElement.hide()
      }
    }, 0)
  }

  handleDropdownShow = (): void => {
    window.addEventListener('keydown', this.handleDropdownKeyDown)
  }

  handleDropdownHide = (): void => {
    window.removeEventListener('keydown', this.handleDropdownKeyDown)
  }

  handleKeyUp = (event: KeyboardEvent): void => {
    if (!this.floatElement) {
      return
    }

    const key = event.key || event.keyCode

    const isEnterKey = checkEnterKey(key)

    if (isEnterKey) {
      if (!this.floatElement.isOpen) {
        this.floatElement.show()

        this.setTargetListeners('add')
      }

      return
    }
  }

  handleDropdownKeyDown = (event: KeyboardEvent): void => {
    if (!this.floatElement) {
      return
    }

    const key = event.key || event.keyCode

    const isEscapeKey = checkEscKey(key)

    if (!this.floatElement.isOpen) {
      return
    }

    if (isEscapeKey) {
      this.element.focus()

      this.setTargetListeners('remove')

      this.floatElement.hide()
    }
  }

  handleFirstTargetFocus = (): void => {
    clearTimeout(this.blurElementTimeoutId)
  }

  handleTargetFocus = (): void => {
    clearTimeout(this.blurLastTargetTimeoutId)
  }

  handleLastTargetBlur = (): void => {
    this.blurLastTargetTimeoutId = setTimeout(() => {
      if (!this.floatElement) {
        return
      }

      this.setTargetListeners('remove')

      this.floatElement.hide()
    }, 0)
  }

  handleTrigger = (event: MouseEvent): void => {
    event.preventDefault()

    this.trigger()
  }

  handleUntrigger = ({ clientX: x, clientY: y, target }): void => {
    if (!this.floatElement) {
      return
    }

    const isCursorOutside = this.floatElement.checkCursorOutside(x, y)
    const isOutsideFloatElements = this.isTargetOutsideFloatElements(target)

    if (isCursorOutside && isOutsideFloatElements) {
      this.floatElement.hide()

      window.removeEventListener('click', this.handleUntrigger)
    }
  }

  setTargetListeners(action): void {
    const { targets, firstTarget, lastTarget } = this.getTargets()

    const setter = action === 'add' ? _addEventListener : _removeEventListener

    setter(firstTarget, 'focus', this.handleFirstTargetFocus)
    targets.forEach(target => setter(target, 'focus', this.handleTargetFocus))
    setter(lastTarget, 'blur', this.handleLastTargetBlur)
    setter(window, 'click', this.handleUntrigger)
  }

  getTargets(): any {
    if (!this.floatElement) {
      return
    }

    const element = this.floatElement.element
    const targets = [...element.querySelectorAll(getFocusableTargets())]
    const firstTarget = targets[0]
    const lastTarget = targets[targets.length - 1]

    return { targets, firstTarget, lastTarget }
  }

  isTargetOutsideFloatElements(target) {
    if (!this.floatElement) {
      return
    }

    const isInsideFloatElement = this.floatElement.contains(target)
    const isInsideFloatElementBtn = this.contains(target)

    return !isInsideFloatElement && !isInsideFloatElementBtn
  }

  getElement(): HTMLElement {
    const elementSelector = `[data-float-element-trigger="${this.dataset.floatElementId}"]`

    return this.querySelector(elementSelector) || this
  }

  trigger = (): void => {
    if (!this.floatElement) {
      return
    }

    this.floatElement.isOpen
      ? this.floatElement.hide()
      : this.floatElement.show()

    window.addEventListener('click', this.handleUntrigger)
  }

  setExpand(isExpanded): void {
    this.setAttribute('data-aria-expanded', isExpanded)
  }

  get floatElement(): Nullable<FloatElement> {
    return document.getElementById(this.dataset.floatElementId)
  }
}

export default DropdownOpener
